import { readonly, ref } from "vue";

import type { CompanyPhoto, CompanyProfile } from "@/entities/company";
import { companyApi } from "@/entities/company";

export type ProfileStatus = "loading" | "ready" | "error";
export type GalleryStatus = "loading" | "ready" | "error";

/**
 * Host-composable сторінки профілю салону (spec §3.1, дзеркало `useMasterDetail`).
 * Робить 1× GET company + 1× GET photos паралельно; гейтить екран двома
 * незалежними статусами. Аплаєри `setCompany`/`setPhotos` — канал зворотного
 * зв'язку від feature-компонентів через @saved / @changed.
 */
export function useSalonProfile() {
  const profileStatus = ref<ProfileStatus>("loading");
  const company = ref<CompanyProfile | null>(null);
  const galleryStatus = ref<GalleryStatus>("loading");
  const photos = ref<CompanyPhoto[]>([]);

  /**
   * Паралельний GET company + GET photos (Promise.allSettled).
   * Статуси незалежні: помилка фото не блокує профіль (spec §3.1).
   */
  async function load(): Promise<void> {
    profileStatus.value = "loading";
    galleryStatus.value = "loading";

    const [companyResult, photosResult] = await Promise.allSettled([
      companyApi.getCompany(),
      companyApi.listPhotos(),
    ]);

    if (companyResult.status === "fulfilled") {
      company.value = companyResult.value;
      profileStatus.value = "ready";
    } else {
      profileStatus.value = "error";
    }

    if (photosResult.status === "fulfilled") {
      photos.value = photosResult.value;
      galleryStatus.value = "ready";
    } else {
      galleryStatus.value = "error";
    }
  }

  /** Retry лише для фото; profileStatus не чіпається. */
  async function reloadGallery(): Promise<void> {
    galleryStatus.value = "loading";
    try {
      photos.value = await companyApi.listPhotos();
      galleryStatus.value = "ready";
    } catch {
      galleryStatus.value = "error";
    }
  }

  /** Аплаєр від @saved форми профілю. */
  function setCompany(c: CompanyProfile): void {
    company.value = c;
  }

  /** Аплаєр від @changed галереї (optimistic + confirmed). */
  function setPhotos(p: CompanyPhoto[]): void {
    photos.value = p;
  }

  return {
    profileStatus: readonly(profileStatus),
    company,
    galleryStatus: readonly(galleryStatus),
    photos,
    load,
    reloadGallery,
    setCompany,
    setPhotos,
  };
}
